import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Filter } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { products, categories } from '../mock/mockData';
import usePageTitle from '../hooks/usePageTitle';

const Products = () => {
  usePageTitle('Steel Products');
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const filteredProducts = products.filter((product) => {
    const matchesCategory = selectedCategory === 'all' || product.category === selectedCategory;
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         product.description.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl lg:text-6xl font-bold text-slate-900 mb-6">
              Our <span className="text-orange-600">Products</span>
            </h1>
            <p className="text-xl text-slate-600 leading-relaxed">
              Comprehensive range of premium steel products for all your construction and industrial needs
            </p>
          </div>
        </div>
      </section>

      {/* Filter Section */}
      <section className="py-8 bg-white border-b border-slate-200 sticky top-[120px] z-40">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            <div className="flex items-center gap-3 w-full md:w-auto">
              <Filter className="w-5 h-5 text-slate-600" />
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger className="w-full md:w-64">
                  <SelectValue placeholder="Select Category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((category) => (
                    <SelectItem key={category.value} value={category.value}>
                      {category.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="relative w-full md:w-96">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                type="text"
                placeholder="Search products..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          {filteredProducts.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredProducts.map((product) => (
                <Card key={product.id} className="group hover:shadow-xl transition-all duration-300 border-slate-200 overflow-hidden">
                  <div className="relative h-64 overflow-hidden">
                    <img
                      src={product.image}
                      alt={product.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute top-4 right-4 bg-orange-600 text-white px-3 py-1 rounded-full text-sm font-medium">
                      {product.category}
                    </div>
                  </div>
                  <CardContent className="p-6">
                    <h3 className="text-2xl font-bold text-slate-900 mb-3">{product.name}</h3>
                    <p className="text-slate-600 mb-4">{product.description}</p>
                    
                    {product.grades && (
                      <div className="mb-4">
                        <div className="text-sm font-semibold text-slate-700 mb-2">Available Grades:</div>
                        <div className="flex flex-wrap gap-2">
                          {product.grades.map((grade, index) => (
                            <span key={index} className="px-3 py-1 bg-slate-100 text-slate-700 rounded-full text-xs font-medium">
                              {grade}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {product.types && (
                      <div className="mb-4">
                        <div className="text-sm font-semibold text-slate-700 mb-2">Types:</div>
                        <div className="flex flex-wrap gap-2">
                          {product.types.map((type, index) => (
                            <span key={index} className="px-3 py-1 bg-slate-100 text-slate-700 rounded-full text-xs font-medium">
                              {type}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {product.sizes && (
                      <div className="mb-4">
                        <div className="text-sm font-semibold text-slate-700 mb-2">Available Sizes:</div>
                        <div className="flex flex-wrap gap-2">
                          {product.sizes.map((size, index) => (
                            <span key={index} className="px-2 py-1 bg-slate-100 text-slate-700 rounded text-xs">
                              {size}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    <Button 
                      onClick={() => navigate('/contact')}
                      className="w-full bg-orange-600 hover:bg-orange-700 text-white mt-4"
                    >
                      Request Quote
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <div className="text-6xl mb-4">🔍</div>
              <h3 className="text-2xl font-bold text-slate-900 mb-2">No Products Found</h3>
              <p className="text-slate-600">Try adjusting your search or filter criteria</p>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto bg-gradient-to-r from-slate-800 to-slate-900 rounded-2xl p-12 text-center">
            <h2 className="text-3xl font-bold text-white mb-4">Need a Custom Solution?</h2>
            <p className="text-xl text-slate-300 mb-8">
              Contact our team for customized steel products and specifications
            </p>
            <Button 
              onClick={() => navigate('/contact')}
              className="bg-orange-600 hover:bg-orange-700 text-white px-8 py-6 text-lg"
            >
              Get in Touch
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Products;
